// src/components/Search/MainContent.js

import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import SearchBar from "./SearchBar";
import ResultsDisplay from "./ResultsDisplay";
import SeasonEpisodeSelector from "./SeasonEpisodeSelector";
import LinksDisplay from "./LinksDisplay";
import DownloadLinksDisplay from "./S1DownloadLinksDisplay";
import S2DownloadLinksDisplay from "./S2DownloadLinksDisplay";
import S3DownloadLinksDisplay from "./S3DownloadLinksDisplay";
import SelectedResultDisplay from "./SelectedResultDisplay";
import DiscoverDisplay from "./DiscoverDisplay";
import apis from "../../services/api";
import { ClipLoader } from "react-spinners";
import serverConfig from "../../config/serverConfig";
import { ResultsContext } from "../../contexts/ResultsContext";
import { ToastContext } from "../../contexts/ToastContext";
import { mapAuthErrors } from "../../utils/errorMapping";
import { AuthContext } from "../../contexts/AuthContext";

export default function MainContent() {
  const { activeServers } = serverConfig;
  const { setHasResults } = useContext(ResultsContext);
  const { addToast } = useContext(ToastContext);
  const { userRole } = useContext(AuthContext);

  // ----------------- State -----------------
  const [type, setType] = useState("Discover"); // 'Discover', 'Stream', or 'Download'
  const [server, setServer] = useState(""); // 'Imdb', 'Tmdb', 'S1', 'S2', 'S3'
  const [results, setResults] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [discoverResults, setDiscoverResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [awaitingSeasonEpisode, setAwaitingSeasonEpisode] = useState(false);
  const [failedCategories, setFailedCategories] = useState([]);
  const [seasonsData, setSeasonsData] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // ----------------- Refs -----------------
  const resultsRef = useRef(null); // Scroll target for newly loaded search results
  const scrollTargetRef = useRef(null); // Scroll target for newly loaded links
  const discoverToastRef = useRef(false);
  const seasonEpisodeSelectorRef = useRef(null);
  const favoritesToastShownRef = useRef(false);

  // --------------------------------------------------
  // Helper: Get Active Servers by Type
  // --------------------------------------------------
  const getActiveServersByType = useCallback(
    (currentType) => {
      const group = serverConfig.serverGroups[currentType];
      return group ? group.filter((s) => activeServers[s]) : [];
    },
    [activeServers]
  );

  // --------------------------------------------------
  // FETCH FAVORITES ON COMPONENT MOUNT
  // --------------------------------------------------
  useEffect(() => {
    const fetchFavorites = async () => {
      setFavoritesLoading(true);
      try {
        const favs = await apis.Favorites.getFavorites();
        setFavorites(favs);
        favoritesToastShownRef.current = false;
      } catch (error) {
        const { response } = error;
        const status = response?.status;
        const errorMessage =
          response?.data?.message || "Failed to load favorites.";

        if (!favoritesToastShownRef.current) {
          if (status === 401 || status === 403 || status === 400) {
            addToast(errorMessage, "info");
          } else {
            addToast(errorMessage, "error");
          }
          favoritesToastShownRef.current = true;
        }
      } finally {
        setFavoritesLoading(false);
      }
    };

    fetchFavorites();
  }, [addToast]);

  // --------------------------------------------------
  // ADD/REMOVE FAVORITE HANDLERS
  // --------------------------------------------------
  const handleAddFavorite = async (item) => {
    try {
      const favoriteItem = {
        id: item.imdbId || item.tmdbId || item.id,
        title: item.title,
        year: item.year,
        photoUrl: item.photoUrl,
        type: item.type,
        source: item.imdbId ? "Imdb" : "Tmdb",
      };

      await apis.Favorites.addFavorite(favoriteItem);
      setFavorites((prev) => [...prev, favoriteItem]);
      addToast(`${favoriteItem.title} added to favorites!`, "success");
    } catch (error) {
      const { response } = error;
      const status = response?.status;
      const errorMessage =
        response?.data?.message ||
        "Sorry, can't add 'Downloads' as Favorites.";

      if (status === 401 || status === 403 || status === 400) {
        addToast(errorMessage, "info");
      } else {
        addToast(errorMessage, "error");
      }
    }
  };

  const handleRemoveFavorite = async (id) => {
    try {
      await apis.Favorites.removeFavorite(id);
      setFavorites((prev) => prev.filter((fav) => fav.id !== id));
      addToast(`Removed from favorites.`, "info");
    } catch (error) {
      const { response } = error;
      const status = response?.status;
      const errorMessage =
        response?.data?.message || "Failed to remove from favorites.";

      if (status === 401 || status === 403 || status === 400) {
        addToast(errorMessage, "info");
      } else {
        addToast(errorMessage, "error");
      }
    }
  };

  // --------------------------------------------------
  // FETCH DISCOVER DATA (WHEN TYPE===DISCOVER)
  // --------------------------------------------------
  useEffect(() => {
    if (type === "Discover") {
      const fetchDiscoverData = async () => {
        const categories = [
          { name: "Top Box Office", endpoint: "GetTopBoxOfficeMovies" },
          { name: "Most Popular Movies", endpoint: "GetMostPopularMovies" },
          { name: "Most Popular TV Shows", endpoint: "GetMostPopularTvShows" },
          { name: "New Movies", endpoint: "GetNewMoviesThisMonth" },
          { name: "New TV Shows", endpoint: "GetNewTvShowsThisMonth" },
        ];

        const resultsObj = {};
        const failedCats = [];
        setLoading(true);
        discoverToastRef.current = false;

        try {
          const api = apis.Imdb; // Always use Imdb for discover
          const fetchPromises = categories.map((category) =>
            api[category.endpoint]().then(
              (data) => ({
                status: "fulfilled",
                category: category.name,
                data,
              }),
              (error) => ({
                status: "rejected",
                category: category.name,
                error,
              })
            )
          );

          const settledResults = await Promise.allSettled(fetchPromises);

          let allFailed = true;

          settledResults.forEach((result) => {
            if (result.status === "fulfilled") {
              const fetchResult = result.value;
              if (fetchResult.data.length > 0) {
                resultsObj[fetchResult.category] = fetchResult.data;
                allFailed = false;
              } else {
                failedCats.push(fetchResult.category);
              }
            } else {
              // Rejected
              failedCats.push(result.reason.category);
            }
          });

          // Show toast if all or partial categories fail
          if (allFailed && !discoverToastRef.current) {
            discoverToastRef.current = true;
            addToast(
              userRole === "User"
                ? "Failed to load Discover content.\nPlease check your connection.\nYou may have reached the usage limit."
                : "Failed to load Discover content.\nMaybe you reached the usage limit,\nPlease login to continue.",
              "error"
            );
          } else if (failedCats.length > 0 && !discoverToastRef.current) {
            discoverToastRef.current = true;
            addToast(
              userRole === "User"
                ? `Some categories failed to load: ${failedCats.join(
                    ", "
                  )}. Check connection or usage limit.`
                : `Some categories failed to load: ${failedCats.join(
                    ", "
                  )}. Please log in to continue.`,
              "error"
            );
          }

          setDiscoverResults(resultsObj);
          setFailedCategories(failedCats);
        } catch (err) {
          if (!discoverToastRef.current) {
            discoverToastRef.current = true;
            addToast(
              userRole === "User"
                ? "An unexpected error occurred.\nPlease check your connection."
                : "An unexpected error occurred.\nMaybe usage limit,\nPlease login to continue.",
              "error"
            );
          }
        }
        setLoading(false);
      };

      fetchDiscoverData();
    }
  }, [type, addToast, userRole]);

  // Reset discover data if switching away from "Discover"
  useEffect(() => {
    if (type !== "Discover") {
      setDiscoverResults({});
      setFailedCategories([]);
    }
  }, [type]);

  // --------------------------------------------------
  // useEffect to Set Server Based on Type
  // --------------------------------------------------
  useEffect(() => {
    const activeServersForType = getActiveServersByType(type);
    if (activeServersForType.length === 1) {
      if (server !== activeServersForType[0]) {
        setServer(activeServersForType[0]);
      }
    } else if (activeServersForType.length > 1) {
      // Only reset server if it's not among the active servers for the current type
      if (!activeServersForType.includes(server)) {
        setServer("");
      }
      // Else, keep the current server
    } else {
      // No active servers, set server to ""
      if (server !== "") {
        setServer("");
      }
    }
  }, [type, server, getActiveServersByType]);

  // --------------------------------------------------
  // HANDLE SEARCH (CALLED FROM SearchBar)
  // --------------------------------------------------
  const handleSearch = async (formValues, searchServer = server) => {
    // If type=Discover => rely on the discover fetch inside useEffect
    if (type === "Discover") {
      resetSearchStates();
      return;
    }

    // If user cleared query => reset states (for Stream/Download)
    if ((type === "Stream" || type === "Download") && !(formValues.query || '').trim()) {
      resetSearchStates();
      return;
    }

    setLoading(true);
    try {
      const api = apis[searchServer];
      if (!api?.searchMoviesOrTvShows) {
        throw new Error(
          `${searchServer} API does not support searchMoviesOrTvShows`
        );
      }

      const data = await api.searchMoviesOrTvShows(formValues);

      // Special case: S1 returns immediate "links" array
      if (type === "Download" && searchServer === "S1") {
        setLinks(data || []);
        setResults([]);
        setSelectedResult(null);

        if (!data || data.length === 0) {
          addToast("No results found.", "info");
        }
      } else {
        // Normal search => display results
        setResults(data || []);
        setSelectedResult(null);
        setLinks([]);

        if (!data || data.length === 0) {
          addToast("No results found.", "info");
        }
      }
    } catch (err) {
      const errorMsg = mapAuthErrors(err);
      addToast(errorMsg, "error");
    }
    setLoading(false);
  };

  // --------------------------------------------------
  // SCROLL TO RESULTS WHEN THEY ARE LOADED
  // --------------------------------------------------
  useEffect(() => {
    // Only scroll if not loading, and we have results (OR if it's an S1 immediate link scenario, results would be empty)
    if (
      !loading &&
      (results.length > 0 ||
        (type === "Download" && server === "S1" && links.length > 0))
    ) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [results, loading, type, server, links.length]);

  // --------------------------------------------------
  // HANDLE RESULT CLICK (FETCH LINKS)
  // --------------------------------------------------
  const handleResultClick = async (result) => {
    setSelectedResult(result);
    setLinks([]);
    setSelectedSeason(null);
    setSelectedEpisode(null);

    // If it's TV => fetch seasons/episodes (Imdb/Tmdb only)
    if (result.type === "tv") {
      setAwaitingSeasonEpisode(true);
      setLoadingSeasons(true);
      setLoading(true);

      try {
        if (server === "Imdb") {
          const data = await apis.Imdb.GetSeasonsAndEpisodes(result.imdbId);
          setSeasonsData(data);
        } else if (server === "Tmdb") {
          const data = await apis.Tmdb.GetSeasonsAndEpisodes(result.tmdbId);
          setSeasonsData(data);
        } else if (type === "Discover") {
          const data = await apis.Imdb.GetSeasonsAndEpisodes(result.imdbId);
          setSeasonsData(data);
        } else {
          addToast(
            "Seasons and Episodes not available for this server.",
            "info"
          );
        }
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(errorMsg || "Failed to fetch seasons/episodes.", "error");
      }

      setLoading(false);
      setLoadingSeasons(false);
      return;
    }

    // If result is a movie => fetch links immediately
    // 1) If in Discover => always use Imdb for streaming links
    if (type === "Discover") {
      setLoading(true);
      try {
        const api = apis.Imdb;
        const params = { imdbId: result.imdbId, type: "movie" };
        const data = await api.getStreamingLinks(params);

        setLinks(data || []);
        if (!data || data.length === 0) {
          addToast("No links found.", "info");
        }
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(
          errorMsg || "An error occurred while fetching discover links.",
          "error"
        );
      }
      setLoading(false);
      return;
    }

    // 2) Stream mode => get streaming links from chosen server
    if (type === "Stream") {
      setLoading(true);
      try {
        const idField = server === "Tmdb" ? "tmdbId" : "imdbId";
        const params = { [idField]: result[idField], type: "movie" };
        const api = apis[server];

        if (!api?.getStreamingLinks) {
          throw new Error(`${server} API does not support getStreamingLinks`);
        }

        const data = await api.getStreamingLinks(params);
        setLinks(data || []);
        if (!data || data.length === 0) {
          addToast("No links found.", "info");
        }
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(
          errorMsg || "An error occurred while fetching streaming links.",
          "error"
        );
      }
      setLoading(false);
      return;
    }

    // 3) Download mode => get download links
    if (type === "Download") {
      setLoading(true);
      try {
        // S2 logic
        if (server === "S2") {
          const params = { movieOrSeriesUrl: result.url };
          const data = await apis.S2.getDownloadLinks(params);
          setLinks(data || []);
          if (!data || data.length === 0) {
            addToast("No links found.", "info");
          } else {
            setSelectedResult({
              ...result,
              type: data.contentType === "Movie" ? "movie" : "tv",
            });
          }
          setLoading(false);
          return;
        }

        // S3 logic
        if (server === "S3") {
          const data = await apis.S3.getDownloadLinks({ url: result.url });
          setLinks(data || []);
          if (!data || data.length === 0) {
            addToast("No links found.", "info");
          } else {
            setSelectedResult({
              ...result,
              type: result.type === "Series" ? "tv" : "movie",
            });
          }
          setLoading(false);
          return;
        }

        // Imdb/Tmdb fallback
        const idField = server === "Tmdb" ? "tmdbId" : "imdbId";
        const params = { [idField]: result[idField], type: "movie" };
        const api = apis[server];

        if (!api?.getDownloadLinks) {
          throw new Error(`${server} API does not support getDownloadLinks`);
        }

        const data = await api.getDownloadLinks(params);
        setLinks(data || []);
        if (!data || data.length === 0) {
          addToast("No links found.", "info");
        }
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(
          errorMsg || "An error occurred while fetching download links.",
          "error"
        );
      }
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // SEASON/EPISODE SELECTION
  // --------------------------------------------------
  const handleSeasonEpisodeSubmit = async (season, episode) => {
    setSelectedSeason(season);
    setSelectedEpisode(episode);
    setLoading(true);

    try {
      const idField = server === "Tmdb" ? "tmdbId" : "imdbId";
      const params = {
        [idField]: selectedResult[idField],
        type: "tv",
        season,
        episode,
      };

      let data;
      const api = apis[type === "Discover" ? "Imdb" : server];

      if (type === "Discover" || type === "Stream") {
        if (!api?.getStreamingLinks) {
          throw new Error(`${server} API does not support getStreamingLinks`);
        }
        data = await api.getStreamingLinks(params);
      } else {
        if (!api?.getDownloadLinks) {
          throw new Error(`${server} API does not support getDownloadLinks`);
        }
        data = await api.getDownloadLinks(params);
      }

      setLinks(data || []);
      if (!data || data.length === 0) {
        addToast("No links found.", "info");
      }
    } catch (error) {
      const errorMsg = mapAuthErrors(error);
      addToast(errorMsg, "error");
    }

    setLoading(false);
  };

  // Focus on season/episode after they load
  useEffect(() => {
    if (
      awaitingSeasonEpisode &&
      seasonsData.length > 0 &&
      seasonEpisodeSelectorRef.current
    ) {
      seasonEpisodeSelectorRef.current.focus();
    }
  }, [awaitingSeasonEpisode, seasonsData]);

  // --------------------------------------------------
  // SCROLL TO LINKS WHEN THEY ARE LOADED
  // --------------------------------------------------
  useEffect(() => {
    // If not loading, and we have either an array of links or contentType
    if (
      !loading &&
      ((Array.isArray(links) && links.length > 0) || links.contentType)
    ) {
      scrollTargetRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [links, loading]);

  // --------------------------------------------------
  // RESET SEARCH STATES
  // --------------------------------------------------
  const resetSearchStates = () => {
    setResults([]);
    setSelectedResult(null);
    setLinks([]);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setAwaitingSeasonEpisode(false);
    setFailedCategories([]);
    setSeasonsData([]);
    // We do NOT reset discoverResults here unless changing mode
  };

  // --------------------------------------------------
  // HELPERS: RANDOM TYPE/SERVER FOR ON-FOCUS (IF DESIRED)
  // --------------------------------------------------
  const randomlySelectTypeAndServer = useCallback(() => {
    const randomType = Math.random() < 0.5 ? "Stream" : "Download";
    let randomServer = "";

    if (randomType === "Stream") {
      const streamServers = ["Imdb", "Tmdb"].filter((s) => activeServers[s]);
      if (streamServers.length > 0) {
        randomServer =
          streamServers[Math.floor(Math.random() * streamServers.length)];
      }
    } else {
      const downloadServers = ["S1", "S2", "S3"].filter((s) => activeServers[s]);
      if (downloadServers.length > 0) {
        randomServer =
          downloadServers[Math.floor(Math.random() * downloadServers.length)];
      }
    }

    // Fallbacks if no servers are active
    if (!randomServer && activeServers.Imdb) randomServer = "Imdb";
    else if (!randomServer && activeServers.S1) randomServer = "S1";

    setType(randomType);
    setServer(randomServer);
    resetSearchStates();
  }, [activeServers]);

  // --------------------------------------------------
  // Update setHasResults (for global "has anything" check)
  // --------------------------------------------------
  useEffect(() => {
    if (type === "Discover") {
      const hasDiscoverResults = Object.keys(discoverResults).some(
        (category) => discoverResults[category].length > 0
      );
      setHasResults(hasDiscoverResults);
    } else {
      setHasResults(results.length > 0);
    }
  }, [type, results, discoverResults, setHasResults]);

  // --------------------------------------------------
  // If user changes mode/server from the SearchBar
  // --------------------------------------------------
  const handleModeOrServerChange = () => {};

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <main className="flex flex-col items-center text-center py-20 px-4 text-white space-y-8 animate-zoomIn">
      <h2 className="text-3xl font-semibold text-accent">
        Find Your Next Movie Night Gem 🎬
      </h2>
      <p className="text-sm text-textPrimary">
        Use the search bar to <strong>{type}</strong> below
      </p>

      {/* SEARCH BAR */}
      <div className="w-full max-w-4xl mx-auto flex flex-col items-center">
        <SearchBar
          type={type}
          server={server}
          onSearch={handleSearch}
          onFocus={() => {
            // If you want auto-switch from Discover to random on focusing the bar:
            if (type === "Discover") randomlySelectTypeAndServer();
          }}
          setType={setType}
          setServer={setServer}
          activeServers={activeServers}
          onModeOrServerChange={handleModeOrServerChange}
        />
      </div>

      {/* LOADING SPINNER */}
      {loading && (
        <div className="flex justify-center items-center">
          <ClipLoader color="#ffffff" loading={loading} size={50} />
        </div>
      )}

      {/* FAVORITES LOADING SPINNER */}
      {favoritesLoading && (
        <div className="flex justify-center items-center">
          <ClipLoader color="#ffffff" loading={favoritesLoading} size={30} />
          <span className="ml-2">Loading favorites...</span>
        </div>
      )}

      {/* DISCOVER RESULTS */}
      {type === "Discover" && !loading && (
        <DiscoverDisplay
          discoverResults={discoverResults}
          onItemClick={handleResultClick}
          failedCategories={failedCategories}
          favorites={favorites}
          onAddFavorite={handleAddFavorite}
          onRemoveFavorite={handleRemoveFavorite}
        />
      )}

      {/* STREAM/DOWNLOAD RESULTS LIST */}
      {(type === "Stream" || type === "Download") &&
        results.length > 0 &&
        !loading &&
        server !== "S1" && (
          <div ref={resultsRef}>
            <ResultsDisplay
              results={results}
              selectedResult={selectedResult}
              onResultClick={handleResultClick}
              server={server}
              favorites={favorites}
              onAddFavorite={handleAddFavorite}
              onRemoveFavorite={handleRemoveFavorite}
            />
          </div>
        )}

      {/* SCROLL TARGET: LINKS SECTION */}
      <div ref={scrollTargetRef} className="w-full max-w-6xl">
        {/* SELECTED RESULT INFO (if in Stream or Discover mode) */}
        {(type === "Stream" || type === "Discover") && selectedResult && (
          <SelectedResultDisplay
            result={selectedResult}
            selectedSeason={selectedSeason}
            selectedEpisode={selectedEpisode}
          />
        )}

        {/* SEASON/EPISODE SELECTOR (for TV) */}
        {selectedResult &&
          selectedResult.type === "tv" &&
          awaitingSeasonEpisode && (
            <SeasonEpisodeSelector
              ref={seasonEpisodeSelectorRef}
              key={`${selectedResult.imdbId || selectedResult.tmdbId}`}
              onSubmit={handleSeasonEpisodeSubmit}
              seasonsData={seasonsData}
              server={server}
              loadingSeasons={loadingSeasons}
            />
          )}

        {/* S1 IMMEDIATE DOWNLOAD LINKS */}
        {Array.isArray(links) && links.length > 0 && server === "S1" && (
          <DownloadLinksDisplay links={links} />
        )}

        {/* S2 DOWNLOAD LINKS */}
        {links && links.contentType && server === "S2" && activeServers.S2 && (
          <S2DownloadLinksDisplay
            links={links}
            selectedResult={selectedResult}
          />
        )}

        {/* S3 DOWNLOAD LINKS */}
        {Array.isArray(links) &&
          links.length > 0 &&
          server === "S3" &&
          activeServers.S3 &&
          selectedResult &&
          selectedResult.type && (
            <S3DownloadLinksDisplay
              links={links}
              selectedResult={selectedResult}
            />
          )}

        {/* GENERIC LINKS DISPLAY (IMDB/TMDB) */}
        {Array.isArray(links) &&
          links.length > 0 &&
          !["S1", "S2", "S3"].includes(server) && (
            <LinksDisplay links={links} />
          )}
      </div>
    </main>
  );
}
